import React, { useEffect, useState } from 'react';
import classes from "./MatchLayoutForPrediction.module.css";
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';

const FT_STATUSES = ["FT", "AET", "PEN"];
const LIVE_STATUSES = ["1H", "2H", "ET", "P", "HT", "LIVE"];


const MatchLayoutForPrediction = ({ match, match_details, index }) => {
    const navigate = useNavigate();
    const [time, setTime] = useState(null);
    const [display, setDisplay] = useState("block");
    
    const {t}=useTranslation();

    const getStatus = (match_details) => {
        const short = match_details?.fixture?.status?.short;
        const elapsed = match_details?.fixture?.status?.elapsed;
        const extra = match_details?.fixture?.status?.extra;

        if (FT_STATUSES.includes(short)) return { label: t("100"), live: false, ft: true };
        if (short === "SUSP") return { label: t("101"), live: false };
        if (short === "PST") return { label: t("102"), live: false };
        if (short === "CANC") return { label: t("103"), live: false };
        if (["ABD", "AWD", "WO"].includes(short)) return { label: t("104"), live: false };
        if (short === "HT") return { label: `${t("105")} ${elapsed}'`, live: true };
        if (short === "ET") return { label: `${elapsed}' ${t("106")} +${extra}'`, live: true };
        if (short === "P") return { label: `${t('107')} ${elapsed}'`, live: true };
        if (LIVE_STATUSES.includes(short)) return { label: `${elapsed}'`, live: true };
        return null;
    };

    useEffect(() => {
        if (display !== "none") {
            const id = setInterval(() => {
                try {
                    const left = moment.duration(moment.utc(match?.match_date_time).diff(moment()));
                    if (left.asMilliseconds() <= 0) { setDisplay("none"); return; }
                    setTime(`${Math.floor(left.asHours())}h ${left.minutes()}m ${left.seconds()}s`);
                } catch (e) { console.log(e); }
            }, 1000);
            return () => clearInterval(id);
        }
    }, [display, match?.match_date_time]);

    const statusObj = getStatus(match_details);
    const showScore = [...FT_STATUSES, ...LIVE_STATUSES].includes(match_details?.fixture?.status?.short);
    const isNS = match?.match_status === "NS";
    const isFT = FT_STATUSES.includes(match_details?.fixture?.status?.short);
    const homeGoals = match_details?.goals?.home;
    const awayGoals = match_details?.goals?.away;

    return (
        <div
            className={classes.card}
            style={{ animationDelay: `${(index ?? 0) * 0.06}s` }}
        >
            {/* top shimmer */}
            <div className={classes.cardShimmer} />

            {/* ── League header ── */}
            <div className={classes.cardHeader}>
                <div className={classes.leagueLeft}>
                    <span className={classes.leagueEmoji}>🏆</span>
                    <span className={classes.leagueName}>{match?.league_name}</span>
                </div>

                {/* status or countdown pill */}
                {statusObj ? (
                    <span className={`${classes.statusPill} ${statusObj.live ? classes.statusPillLive : classes.statusPillFt}`}>
                        {statusObj.live && <span className={classes.liveDot} />}
                        {statusObj.label}
                    </span>
                ) : isNS && time && display !== "none" ? (
                    <span className={classes.timerPill}>
                        <span className={classes.timerDot} />
                        {time}
                    </span>
                ) : null}
            </div>

            {/* ── Teams + score ── */}
            <div className={classes.teamsRow}>
                {/* Home */}
                <div className={classes.team}>
                    <div className={`${classes.logoWrap} ${isFT && homeGoals > awayGoals ? classes.logoWrapWinner : ""}`}>
                        <img src={match?.home_team_image} alt={match?.home_team_name} className={classes.teamLogo} />
                    </div>
                    <span className={classes.teamName}>{match?.home_team_name}</span>
                </div>

                {/* Centre — score or VS */}
                <div className={classes.centreBlock}>
                    {showScore ? (
                        <>
                            <div className={classes.scoreRow}>
                                <span className={`${classes.scoreNum} ${isFT && homeGoals > awayGoals ? classes.scoreWin : isFT && homeGoals < awayGoals ? classes.scoreLose : ""}`}>
                                    {homeGoals ?? "–"}
                                </span>
                                <span className={classes.scoreDash}>—</span>
                                <span className={`${classes.scoreNum} ${isFT && awayGoals > homeGoals ? classes.scoreWin : isFT && awayGoals < homeGoals ? classes.scoreLose : ""}`}>
                                    {awayGoals ?? "–"}
                                </span>
                            </div>
                            {isFT && <span className={classes.ftLabel}>{t("136")}</span>}
                        </>
                    ) : (
                        <>
                            <span className={classes.vsText}>{t("137")}</span>
                            <span className={classes.kickoffDate}>
                                {moment.utc(match?.match_date_time).local().format("ddd DD MMM")}
                            </span>
                            <span className={classes.kickoffTime}>
                                {moment.utc(match?.match_date_time).local().format("HH:mm")}
                            </span>
                        </>
                    )}
                </div>

                {/* Away */}
                <div className={classes.team}>
                    <div className={`${classes.logoWrap} ${isFT && awayGoals > homeGoals ? classes.logoWrapWinner : ""}`}>
                        <img src={match?.away_team_image} alt={match?.away_team_name} className={classes.teamLogo} />
                    </div>
                    <span className={classes.teamName}>{match?.away_team_name}</span>
                </div>
            </div>

            {/* ── What you win strip ── */}
            <div className={classes.rewardsStrip}>
                <span className={classes.rewardsLabel}>{t("138")}</span>
                <div className={classes.rewardPills}>
                    {[`${t("139")}`, `${t("140")}`, `${t("141")}`].map(r => (
                        <span key={r} className={classes.rewardPill}>{r}</span>
                    ))}
                </div>
            </div>

            {/* ── CTA button ── */}
            <div className={classes.cardFooter}>
                <button
                    type="button"
                    className={classes.ctaBtn}
                    onClick={() => navigate("/prediction-game", {
                        state: { match_fixture_id: match?.match_fixture_id }
                    })}
                >
                    <span className={classes.ctaBtnGlow} />
                    <span className={classes.ctaBtnText}>
                        {isFT ? t("142") : t("143")}
                    </span>
                    <FaArrowRight className={classes.ctaBtnArrow} />
                </button>
            </div>
        </div>
    );
};

export default MatchLayoutForPrediction;